<template>
  <div>
    <div>
      <div class="font-weight-bold mb-1 mt-5">
        Qualifications
      </div>
      <h2 class="mb-5">
        What qualifications do you have?
      </h2>
    </div>
    <div v-if="isFetchingUserQualifications">
      <v-skeleton-loader v-for="i in 3" :key="i" type="list-item" />
    </div>
    <div
      v-else
      v-for="qualification in currentUserQualifications"
      :key="qualification.id"
      class="d-flex justify-space-between mb-5"
    >
      <div class="qualifications_summary">
        <svg
          class="qualifications-icon"
          width="24"
          height="37"
          viewBox="0 0 24 37"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M22.25 11.5C22.25 17.3184 17.6343 22 11.9834 22C6.33254 22 1.7168 17.3184 1.7168 11.5C1.7168 5.68158 6.33254 1 11.9834 1C17.6343 1 22.25 5.68158 22.25 11.5Z"
            stroke="black"
            stroke-width="2"
          />
          <path
            d="M17.3514 11.5C17.3514 14.557 14.9288 17 11.9833 17C9.03784 17 6.61523 14.557 6.61523 11.5C6.61523 8.44301 9.03784 6 11.9833 6C14.9288 6 17.3514 8.44301 17.3514 11.5Z"
            stroke="black"
            stroke-width="2"
          />
          <path
            d="M6.5957 35.3982H5.5957C5.5957 35.8011 5.83744 36.1646 6.20895 36.3204C6.58046 36.4762 7.00918 36.3939 7.29654 36.1115L6.5957 35.3982ZM6.5957 21L7.08228 20.1264C6.77256 19.9539 6.39467 19.9582 6.08903 20.1379C5.7834 20.3175 5.5957 20.6455 5.5957 21H6.5957ZM11.9841 30.1041L12.6849 29.3908C12.2959 29.0085 11.6723 29.0085 11.2832 29.3908L11.9841 30.1041ZM17.3725 35.3982L16.6716 36.1115C16.959 36.3939 17.3877 36.4762 17.7592 36.3204C18.1307 36.1646 18.3725 35.8011 18.3725 35.3982H17.3725ZM17.3725 21.0004H18.3725C18.3725 20.6459 18.1848 20.3179 17.8792 20.1383C17.5735 19.9586 17.1957 19.9542 16.8859 20.1267L17.3725 21.0004ZM7.5957 35.3982V21H5.5957V35.3982H7.5957ZM11.2832 29.3908L5.89486 34.6849L7.29654 36.1115L12.6849 30.8174L11.2832 29.3908ZM18.0733 34.6849L12.6849 29.3908L11.2832 30.8174L16.6716 36.1115L18.0733 34.6849ZM16.3725 21.0004V35.3982H18.3725V21.0004H16.3725ZM16.8859 20.1267C15.4296 20.9377 13.7608 21.3982 11.9844 21.3982V23.3982C14.1115 23.3982 16.1141 22.8457 17.859 21.8741L16.8859 20.1267ZM11.9844 21.3982C10.2078 21.3982 8.53873 20.9375 7.08228 20.1264L6.10913 21.8736C7.85415 22.8455 9.85708 23.3982 11.9844 23.3982V21.3982Z"
            fill="black"
          />
        </svg>
        <div class="qualifications_summary__text">
          <div class="qualifications_summary__text__title">
            {{ qualification.qualificationType }} -
            {{ qualification.qualificationSubject }} ({{
              qualification.qualificationGrade
            }})
          </div>
          <div class="qualifications_summary__text__subtitle">
            {{ qualification.qualificationInstitution }}
          </div>
        </div>
      </div>
      <div class="d-flex align-center">
        <EditQualificationAction :qualification="qualification" class="mr-2" />
        <div
          class="text-center "
          v-if="includes(itemsToRemove, qualification.id)"
        >
          <v-progress-circular size="30" indeterminate color="primary" />
        </div>
        <div @click="() => onRemove(qualification.id)" v-else>
          <v-icon>mdi-close</v-icon>
        </div>
      </div>
    </div>
    <AddQualificationAction />
    <div class="d-flex justify-end">
      <PrimaryButton @click.native="nextStep">
        {{ submitButtonTitle || "Continue" }}
      </PrimaryButton>
    </div>
  </div>
</template>

<script>
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import EditQualificationAction from "@/views/profile/QuestionsAndAnswers/steps/Qualifications/actions/EditQualificationAction";
import AddQualificationAction from "@/views/profile/QuestionsAndAnswers/steps/Qualifications/actions/AddQualificationAction";
import { QUALIFICATIONS_NAMESPACE } from "@/store/modules/users/qualifications";
import { createNamespacedHelpers } from "vuex";
import {
  FETCH_USER_QUALIFICATIONS,
  REMOVE_USER_QUALIFICATION
} from "@/store/modules/users/qualifications/actions";
import { copyState } from "@/utils/helpers";
import { AUTH_NAMESPACE } from "@/store/modules/auth";
import { filter, includes } from "lodash";

const {
  mapActions,
  mapState: mapQualificationsState
} = createNamespacedHelpers(QUALIFICATIONS_NAMESPACE);
const { mapState: mapAuthState } = createNamespacedHelpers(AUTH_NAMESPACE);

export default {
  name: "Qualifications",
  props: {
    submitButtonTitle: String
  },
  components: {
    PrimaryButton,
    EditQualificationAction,
    AddQualificationAction
  },
  created() {
    this.emitProgressValue(100);
    this.fetchQualifications(this.self.id);
  },
  data() {
    return {
      isLoading: false,
      itemsToRemove: []
    };
  },
  computed: {
    ...mapAuthState({
      self: state => state.userData
    }),
    ...mapQualificationsState(
      copyState(["isFetchingUserQualifications", "currentUserQualifications"])
    )
  },
  methods: {
    ...mapActions({
      removeQualification: REMOVE_USER_QUALIFICATION,
      fetchQualifications: FETCH_USER_QUALIFICATIONS
    }),
    async onRemove(idToRemove) {
      this.itemsToRemove = [...this.itemsToRemove, idToRemove];
      await this.removeQualification(idToRemove);
      this.itemsToRemove = filter(this.itemsToRemove, idToRemove);
    },
    nextStep() {
      // progressValue bigger than 100 to trigger next step function
      this.emitProgressValue(101);
      this.$emit("close");
    },
    emitProgressValue(value) {
      this.$emit("onStepChanged", {
        progressValue: value
      });
    },
    stepBack() {
      this.emitProgressValue(0);
    },
    includes
  }
};
</script>

<style lang="scss">
.qualifications_summary {
  svg {
    float: left;
    margin-top: 5px;
    margin-right: 10px;
  }

  &__text {
    float: left;

    &__title {
      font-weight: bold;
    }

    &__subtitle {
      color: $secondary-text;
    }
  }
}
</style>
